import util from '@enigma/core/utilities';
import { firestore, realtime } from '@enigma/core/services';
import { user } from '@enigma/core/services';
const origin = "gmgroup.fleet";
/**
 * core.components.tables.engine API
 */
export const addNewRecord = async (record, tableMetadata) => {
    // add new record to global archive
    const recordID = (await firestore.collection(tableMetadata.collection).add(record)).id;
    // update current users device db
    const database = new util.database(origin);
    const table = database.table(tableMetadata.id);
    await table.document(recordID).set(record);
    const databaseRef = realtime.ref(`gmgroup/fleet`);
    // notify change event for other users
    databaseRef.push({
        recordID: recordID,
        tableID: tableMetadata.id,
        data: record,
        timestamp: Date.now(),
        type: "added",
        userID: user.uid,
        syncedList: [user.uid]
    });
}
/**
 * Update table record
 */
export const updateRecord = async (recordID, record, tableMetadata, config) => {
    return new Promise(
        async (resolve, reject) => {
            firestore.collection(tableMetadata.collection)
                .doc(recordID).update(record).then(_ => resolve(), _ => reject())
            // update current users device db
            const database = new util.database(origin);
            const table = database.table(tableMetadata.id);
            await table.document(recordID).update(record);
            realtime.ref(`gmgroup/fleet`)
                .push({
                    recordID: recordID,
                    tableID: tableMetadata.id,
                    userID: user.uid,
                    data: record,
                    timestamp: Date.now(),
                    type: "modified",
                    syncedList: [user.uid]
                }).then(_ => resolve());
        }
    )
}
/**
 * Update table record
 */
export const deleteRecord = async (recordID, tableMetadata, config) => {
    return new Promise(
        async (resolve, reject) => {
            firestore.collection(tableMetadata.collection)
                .doc(recordID).delete().then(async _ => { resolve() }, _ => reject())
            // update current users device db
            const database = new util.database(origin);
            const table = database.table(tableMetadata.id);
            await table.document(recordID).delete();
            realtime.ref(`gmgroup/fleet`)
                .push({
                    recordID: recordID,
                    tableID: tableMetadata.id,
                    userID: user.uid,
                    timestamp: Date.now(),
                    type: "removed",
                    syncedList: [user.uid]
                }).then(_ => resolve());
        }
    )
}
/**
 * Get min table record range. Minimum record
 * ranges are a month prior to the curent date
 */
export const getMinRange = _ => {
    // get current date
    const currentDate = new util.cdate(Date.now());
    currentDate.decrementMonth(1);
    return currentDate.dateObject.getTime();
}
/**
 * Extracts search term from the url
 */
export const getSearch = location => {
    const search = location.search;
    let selected = [];
    const columns = search.split('&').find((query) =>
        query.search('search=') > -1 ? true : false);
    if (columns) {
        const current = columns.split('search=')[1].split(',');
        if (current[0] === "") current.splice(0, 1);
        for (const query of current)
            selected.push(`${query}`.toLowerCase())
    }
    return selected;
}
/**
 * Extracts columns from the url
 */
export const getSelectedColumns = location => {
    const search = location.search;
    let selected = [];
    const columns = search.split('&').find((query) =>
        query.search('column=') > -1 ? true : false);
    if (columns) {
        const current = columns.split('column=')[1].split(',');
        if (current[0] === "") current.splice(0, 1);
        for (const column of current)
            selected.push(column)
    }
    return selected;
}
/**
 *  Extracts selected records from the url
 */
export const getSelectedRecords = location => {
    const search = location.search;
    let selected = [];
    const records = search.split('&').find((query) =>
        query.search('select=') > -1 ? true : false);
    if (records) {
        const current = records.split('select=')[1].split(',');
        if (current[0] === "") current.splice(0, 1);
        for (const recordID of current)
            selected.push({ id: recordID })
    }
    return selected;
}
/**
 * Normalize the record ranges to specific time series
 */
export const getRecordRange = (timestamp, range) => {
    const stamp = new util.cdate(timestamp);
    const current = new util.cdate(Date.now());
    // check if max date is set to the current date
    const start = new Date(`${current.format("mm-dd-yyyy")} 00:00`).getTime();
    const end = new Date(`${current.format("mm-dd-yyyy")} 23:59`).getTime();
    if (timestamp >= start && timestamp <= end) {
        if (range === "max") return end;
        return start;
    }
    return (new Date(`${stamp.format("mm-dd-yyyy")} ${range === "min" ? "00:00" : "23:59"}`)).getTime();
}
/**
 * Get active record set
 */
export const getRecordSet = (report, records) => {
    // report records take priority before all records
    if (report.records.length > 0) return report.records;
    // use all records
    else return records;
}
/**
 * Get records that match the search term
 */
export const getSearchRecords = (location, selected, report, records, schema) => {
    let result = [];
    const search = getSearch(location);
    if (search.length > 0) {
        // check if current record matches search
        let columns = selected.columns.length > 0
            ? selected.columns : schema;
        // normalize search term
        const searchTerm = `${search[0]}`.replaceAll(/[\\()]/g, "");
        // cycle through records
        for (const record of getRecordSet(report, records)) {
            // match selected columns
            for (const column of columns) {
                // identify column
                const columnName = column.name || column;
                let columnValue = record[columnName];
                // identify column type
                const selectedColumn = schema?.find(({ name }) => name === columnName);
                // identify searchable value
                if (!selectedColumn) {
                    if (columnName === "id") columnValue = record.id;
                    if (columnName === "author") columnValue = record.author;
                    if (columnName === "timestamp") columnValue =
                        new Date(record.timestamp).toDateString();
                } else if (selectedColumn.type === "file"
                    && record[columnName]?.name)
                    columnValue = record[columnName].name;
                else if (selectedColumn.type === "date"
                    && !isNaN(new Date(record[columnName]))) {
                    columnValue = new util.cdate(record[columnName])
                        .format("mm-dd-yyyy", "/");
                }
                // show record if current custom column matches search
                if (`${columnValue}`.toLowerCase().search(searchTerm) > -1)
                    result.push(record)
            }
            // check if static column matches search if no column is selected
            if (selected.columns.length === 0) {
                if (`${record.id}`.toLowerCase().search(searchTerm) > -1
                    || `${record.branch}`.toLowerCase().search(searchTerm) > -1
                    || `${record.author}`.toLowerCase().search(searchTerm) > -1
                    || `${new Date(record.timestamp).toDateString()}`
                        .toLowerCase().search(searchTerm) > -1)
                    result.push(record)
            }
        }
    }
    // no records match search or no search in progress
    return result;
}
/**
 * Fetch all records between the record ranges
 */
export const fetchRecords = async (minRecordRange, maxRecordRange, metadata, config) => {
    // local database refferece
    const database = new util.database(origin);
    // check if local cache has been initilized
    const table = database.table(metadata.id);
    // get records within the requested range
    let query = config?.custom
        ? await table.index('timestamp')
            .where("timestamp", ">=", minRecordRange)
            .and("timestamp", "<=", maxRecordRange).get()
        : await table.get();
    // order resultsz
    const collection = query.orderBy("timestamp", "DESC");
    if (collection.empty) return [];
    // return all table records
    else {
        let result = []
        for (const record of collection.getAll())
            result.push(record.data)
        return result;
    }
}
/**
 * Perform hard Sync current range records on device.
 * @param config Table configurations
 * @param metadata Table metadata
 * @param minRecordRange Current Minimum record range
 * @param maxRecordRange Current Maximum record range
 * @param onChange Function to call when a change event is triggered
 */
export const manualSync = async (config, metadata, minRecordRange, maxRecordRange) => {
    // local database refferece
    const database = new util.database(origin);
    // check if local cache has been initilized
    const table = database.table(metadata.id);
    console.log("performing manual sync on record range");
    let criteria = firestore.collection(metadata.collection);
    // filter table records on custom criteria if provided
    if (config.recordCriteria) {
        criteria = criteria
            .where(config.recordCriteria.property, "==", config.recordCriteria.value)
    }
    criteria = criteria
        .where('timestamp', '>=', minRecordRange)
        .where('timestamp', "<=", maxRecordRange);
    console.log("removing all records within range")
    // remove all records within record range
    const currentRecords = await table
    .where('timestamp', '>=', minRecordRange)
    .and('timestamp', '<=', maxRecordRange).get();
    for (const record of currentRecords.getAll())
        await record.delete();
    console.log("all records removed")
    // sync records on device
    const records = await criteria.get();
    for (const record of records.docs)
        await table.document(record.id).set(record.data());
    // update synced state
    console.log("specified range synced, fetching from device db");
}
/**
 * Sync current range records on device. Note records are synced during
 * the begining of each session for each table.
 * @param config Table configurations
 * @param metadata Table metadata
 * @param minRecordRange Current Minimum record range
 * @param maxRecordRange Current Maximum record range
 * @param onChange Function to call when a change event is triggered
 */
export const sync = async (config, metadata, minRecordRange, maxRecordRange, onChange) => {
    // local database refferece
    const database = new util.database(origin);
    // check if local cache has been initilized
    const table = database.table(metadata.id);
    // identify sync in progress
    let cache = JSON.parse(localStorage.getItem(origin)) || {};
    // check if the record range has not already been synced
    if (!cache[metadata.id]
        || minRecordRange < cache[metadata.id].minRecordRange
        || maxRecordRange > cache[metadata.id].maxRecordRange) {
        console.log("specified range is not synced, syncing the records");
        let criteria = firestore.collection(metadata.collection);
        // filter table records on custom criteria if provided
        if (config.recordCriteria) {
            criteria = criteria
                .where(config.recordCriteria.property, "==", config.recordCriteria.value)
        }
        // sync only records that have not been synced
        if (!cache[metadata.id]) {
            console.log("no records synced for table yet, syncing all")
            // no records synced for table yet, fetch all within range
            criteria = criteria.where('timestamp', '>=', minRecordRange)
                .where('timestamp', "<=", maxRecordRange);
            // set synced record range
            cache[metadata.id] = { maxRecordRange: maxRecordRange, minRecordRange: minRecordRange }
        } else if (minRecordRange < cache[metadata.id].minRecordRange) {
            console.log('syncing excess records before the minimum synced records')
            // minimum range shifted
            criteria = criteria.where('timestamp', '>=', minRecordRange)
                .where('timestamp', "<=", cache[metadata.id].minRecordRange)
            // update minimum synced record range
            cache[metadata.id].minRecordRange = minRecordRange;
        } else {
            console.log('syncing excess records after the maximum synced records');
            // maximum range sifted
            criteria = criteria.where('timestamp', '<=', maxRecordRange)
                .where('timestamp', ">=", cache[metadata.id].minRecordRange)
            // update maximum synced record range
            cache[metadata.id].maxRecordRange = maxRecordRange;
        }
        // sync records on device
        const records = await criteria.get();
        for (const record of records.docs)
            await table.document(record.id).set(record.data());
        // update synced state
        localStorage.setItem(origin, JSON.stringify(cache));
    }
    console.log("specified range synced, fetching from device db");
    // check if records exits for current range
    let query = config?.custom
        ? await table.index('timestamp')
            .where("timestamp", ">=", minRecordRange)
            .and("timestamp", "<=", maxRecordRange).limit(1).get()
        : await table.get(1);
    if (query.empty) onChange(false);
    else onChange(true);
}